import React, { Component, ComponentType } from 'react';
import Translation from '../components/UI/Translation';
import { CheckboxOptionType } from 'antd/lib/checkbox/Group';
import TableCellTypes from '../helpers/tableCellTypes';
import { variables } from '../styles/variables';
import { HeaderElement, GenerateExpandableTableSetup, CallbackWithArg } from '../interfaces/global';
import { Tooltip } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import scrollIntoView from 'scroll-into-view';

export interface WithCommonTableLogicProps {
  columnDataWithTranslation: (activeColumns: Array<string>, columns: SingleColumn[]) => SingleColumn[];
  generateTableContent: (tableData: SingleObjectDataStructure[], columns: SingleColumn[]) => SingleObjectDataStructure[];
  convertDataForCheckboxes: (data: any[]) => CheckboxOptionType[];
  convertDataForFilters: (filters: FilterData[]) => any[];
  sortMe: (sorter: any, onSort: CallbackWithArg) => void;
  expandMe: (expanded: any, onDetailsGetting: CallbackWithArg, onDetailsSetting: CallbackWithArg) => void;
  onSelectedRow: (selected: number[], key: number, onClick: () => void) => object;
  generateTableHeaders: (total: number, availableRecordsAmount: number, selectedAmount: number) => HeaderElement[];
  // generateNonBreakColumns: (columns: any[]) => SingleColumn[];
  generateExpandableTableSetup: GenerateExpandableTableSetup;
  // removeUnwantedColumns: (columns: SingleColumn[], unwantedColumns: string[]) => SingleColumn[];
  addFixedColumn: (columns: SingleColumn[]) => SingleColumn[];
  scrollToTableRow: (rowKey: string | number, tableId: string) => void;
  allowScrollToTableRow: () => void;
  scrollShouldBeFired: boolean;
  moveToOtherRecord: (targetRecordIndex: number, data: SingleObjectDataStructure[], tableId: string) => void;
}

interface State {
  scrollShouldBeFired: boolean;
}

/**
 * HOC dostarczający powszechnie wykorzystywane w zakładkach z tabelami funkcjonalności konwersji danych w celu ich prawidłowego do wyświetlania.
 *
 * @param Cmp
 */
function withCommonTableLogic(Cmp: ComponentType<any>): any {
  class Controller extends Component<any, State> {
    constructor(props: any) {
      super(props);
      this.state = {
        scrollShouldBeFired: false,
      } as State;
    }
    /**
     * Funkcja konwertująca dane do wyświetlenia translacji dla kolumn
     *
     * @param {string[]} activeColumns
     * @param {any[]} columns
     * @memberof Controller
     */
    columnDataWithTranslation = (activeColumns: string[], columns: any[]): SingleColumn[] => {
      return columns
        .map(column => {
          const isActive = activeColumns.find((columnName: string) => columnName === column.dataIndex);
          return isActive ? { ...column, title: <Translation id={column.title} /> } : null;
        })
        .filter(column => column !== null);
    };
    /**
     * Funkcja konwertująca dane do wyświetlenia translacji dla pól wielokrotnego wyboru
     *
     * @param {any[]} data
     * @memberof Controller
     */
    convertDataForCheckboxes = (data: any[]): CheckboxOptionType[] => {
      return data.map(option => ({
        label: <Translation id={`${option.nazwa ? option.nazwa : option.title}`} />,
        value: option.dataIndex ? option.dataIndex : option.value ? option.value : '',
      }));
    };
    /**
     * Funkcja generująca odpowiedni kontent za pomocą klasy TableCellTypes w zależności od typu danych
     *
     * @param {SingleObjectDataStructure[]} tableData
     * @param {SingleColumn[]} columns
     * @param {((() => void) | undefined)} [callback=undefined]
     * @memberof Controller
     */
    public generateTableContent = (
      tableData: SingleObjectDataStructure[],
      columns: SingleColumn[],
    ): SingleObjectDataStructure[] => {
      return tableData.map(item => {
        const modifiedItem = { ...item };
        Object.entries(modifiedItem).forEach(([key, value]) => {
          if (value !== null) {
            columns.forEach(col => {
              if (col.dataIndex === key) {
                modifiedItem[key] = TableCellTypes.generate({ type: col.type, data: value });
              }
            });
          }
        });
        return modifiedItem;
      });
    };
    /**
     * Funkcja sortująca dane w tabeli "po backendzie"
     *
     * @param {*} sorter
     * @param {CallbackWithArg} onSort
     * @memberof Controller
     */
    sortMe = (sorter: any, onSort: CallbackWithArg): void => {
      const { field, order } = sorter;
      onSort({
        columnName: order ? field : undefined,
        order: order ? order : undefined,
      });
    };
    // info - aby funkcja działała prawidłowo należy nadać id tabeli, w której chce się stosować scrollowanie do wiersza - ten sam, co podawany w parametrze tableId poniszej funkcji
    scrollToTableRow = (rowKey: number | string, tableId: string): void => {
      scrollIntoView(document.querySelector(`#${tableId} [data-row-key="${rowKey}"]`) as HTMLElement, {
        align: {
          top: 0,
          left: 0,
        },
      });
      this.setState(prev => ({ ...prev, scrollShouldBeFired: false }));
    };
    allowScrollToTableRow = (): void => {
      this.setState(prev => ({ ...prev, scrollShouldBeFired: true }));
    };
    moveToOtherRecord = (targetRecordIndex: number, data: SingleObjectDataStructure[], tableId: string): void => {
      if (targetRecordIndex < data.length || targetRecordIndex > -1) {
        const targetRecordData = data.find((_record: any, idx: number) => idx === targetRecordIndex);
        if (targetRecordData) {
          const targetRow = document.querySelector(`#${tableId} [data-row-key="${targetRecordData.key}"]`);
          if (targetRow) {
            targetRow.dispatchEvent(new Event('click', { bubbles: true }));
            this.allowScrollToTableRow();
          }
        }
        // }
      }
    };
    /**
     * Funkcja wykonująca przekazany callback przy rozszerzaniu widoku wiersza tabeli
     *
     * @param {any[]} expanded
     * @param {CallbackWithArg} onDetailsGetting
     * @param {CallbackWithArg} onDetailsSetting
     * @memberof Controller
     */

    expandMe = (expanded: any[], onDetailsGetting: CallbackWithArg, onDetailsSetting: CallbackWithArg): void => {
      if (expanded.length === 1) {
        onDetailsGetting(expanded[0]);
        this.allowScrollToTableRow();
      }
      onDetailsSetting(expanded);
    };
    /**
     * Funkcja zwracająca styl wiersza oraz akcję na zaznaczeniu wiersza w tabeli
     *
     * @param {number[]} selected
     * @param {number} key
     * @param {() => void} onClick
     * @memberof Controller
     */
    onSelectedRow = (selected: number[], key: number, onClick: () => void): object => {
      return {
        style: {
          backgroundColor: selected.includes(key) ? variables.colorBackground : variables.colorWhite,
          cursor: 'pointer',
        },
        onClick: onClick,
      };
    };
    /**
     * Funkcja konwertująca dane do wyświetlenia translacji w filtrach
     *
     * @param {FilterData[]} filters
     * @memberof Controller
     */
    convertDataForFilters = (filters: FilterData[]): any[] => {
      return filters.map(filter => ({
        ...filter,
        title: <Translation id={`${filter.title}`} />,
        values: this.convertDataForCheckboxes(filter.values),
      }));
    };
    addFixedColumn = (columns: SingleColumn[]): SingleColumn[] => {
      const cols = [...columns];
      cols.push({
        title: '',
        key: 'info',
        dataIndex: 'info',
        fixed: 'right',
        width: 60,
        sorter: false,
        render: (_: any, record: SingleObjectDataStructure) =>
          record.formyZajec && record.formyZajec.czyKierownik ? (
            <Tooltip title={<Translation id='PRZYDZIAL_INFO_YOUAREMANAGER' />}>
              <UserOutlined />
            </Tooltip>
          ) : null,
      });
      return cols;
    };
    /**
     * Fukcja generująca obiekt z danymi do headera tabeli
     *
     * @param {number} total
     * @param {number} availableRecordsAmount
     * @param {number} selectedAmount
     * @memberof Controller
     */
    generateTableHeaders = (total: number, availableRecordsAmount: number, selectedAmount: number): HeaderElement[] => [
      {
        title: <Translation id='TABLE_HEADER_TOTAL' />,
        info: total,
      },
      {
        title: <Translation id='TABLE_HEADER_DISPLAYED' />,
        info: availableRecordsAmount,
      },
      {
        title: <Translation id='TABLE_HEADER_SELECTED' />,
        info: selectedAmount,
      },
      {
        title: <Translation id='TABLE_HEADER_REMAINDER' />,
        info: total - availableRecordsAmount,
      },
    ];
    /**
     * Funkcja generująca obiekt konfiguracyjny expandable dla tabeli Ant Design
     *
     * @param {*} selected
     * @param {*} rowDetailsViewCallback
     * @param {*} getDataCallback
     * @param {*} setSelectedCallback
     * @param {boolean} [requiresRecordId=false]
     * @type {GenerateExpandableTableSetup}
     * @memberof Controller
     */
    generateExpandableTableSetup: GenerateExpandableTableSetup = (
      selected,
      rowDetailsViewCallback,
      getDataCallback,
      setSelectedCallback,
      requiresRecordId = false,
    ) => {
      const commonExpandable = {
        defaultExpandedRowKeys: selected,
        expandedRowKeys: selected,
        expandedRowRender: rowDetailsViewCallback,
      };
      if (requiresRecordId)
        return {
          ...commonExpandable,
          onExpand: (expanded: boolean, record: any): void => {
            setSelectedCallback([record.key], expanded);
            getDataCallback(record.key);
          },
        };
      else
        return {
          ...commonExpandable,
          onExpandedRowsChange: (expanded: any, record: any): void => {
            this.expandMe(expanded, getDataCallback, setSelectedCallback);
          },
        };
    };
    render(): React.ReactNode {
      return (
        <Cmp
          {...this.props}
          columnDataWithTranslation={this.columnDataWithTranslation}
          convertDataForCheckboxes={this.convertDataForCheckboxes}
          generateTableContent={this.generateTableContent}
          convertDataForFilters={this.convertDataForFilters}
          sortMe={this.sortMe}
          expandMe={this.expandMe}
          onSelectedRow={this.onSelectedRow}
          generateTableHeaders={this.generateTableHeaders}
          generateExpandableTableSetup={this.generateExpandableTableSetup}
          addFixedColumn={this.addFixedColumn}
          moveToOtherRecord={this.moveToOtherRecord}
          scrollToTableRow={this.scrollToTableRow}
          allowScrollToTableRow={this.allowScrollToTableRow}
          scrollShouldBeFired={this.state.scrollShouldBeFired}
        />
      );
    }
  }
  return Controller;
}

export default withCommonTableLogic;
